import React from 'react';
import { ModalsKeys } from 'global';
import { Add } from '@mui/icons-material';
import { useModal } from 'hooks/useModal';
import makeStyles from '@mui/styles/makeStyles';
import { Box, Button, Tooltip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { ProposalProduct } from '@trnsact/trnsact-shared-types/dist/generated';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { ProposalProductsCard } from 'modules/desking';
import { ProductConfig, ProposalProductCardModes } from 'modules/desking/types';
import { prisingConfigByVendor } from '../../../constants';
import { SelectedProductsList } from './SelectedProductsList';
import { menuConstructorActions, menuConstructorSelectors } from '../../../model';
import { useAftermarketPermissions } from '../../../hooks/useAftermarketPermissions';
import { getProductFactHashMapKey } from 'modules/desking/lib';

interface Props {
  isProductsLoading: boolean;
}

export const ProductsCard = ({ isProductsLoading }: Props) => {
  const classes = useStyles();

  const dispatch = useDispatch();

  const isLoading = useSelector(menuConstructorSelectors.isLoading);
  const selectedMenuOption = useSelector(menuConstructorSelectors.selectedMenuOption) as any;
  const allProducts = useSelector(menuConstructorSelectors.proposalsProducts) as ProposalProduct[];
  const selectedProducts = useSelector(menuConstructorSelectors.selectedProposalsProducts) as ProposalProduct[];
  const proposalProductConfigurations = useSelector(menuConstructorSelectors.proposalProductConfigurations);
  const proposalProductConfigurationsInMenu = useSelector(menuConstructorSelectors.proposalProductConfigurationsInMenu);
  const selectedProductsFactsForCheck = useSelector(menuConstructorSelectors.selectedProductsFactsForCheck);
  const selectedProductsConfigurations = useSelector(menuConstructorSelectors.selectedProductsConfigurations);

  const isMinMaxRetailCostInvalid = Object.values(selectedProductsConfigurations).some(
    pc => !pc.isMinMaxRetailCostValid
  );

  const { handleOpen: handleProposalProductsDialogOpen } = useModal(ModalsKeys.ProposalProductsDialog);
  const { hasWriteAccess } = useAftermarketPermissions();

  const handleProductsDialogConfirm = (selectedProducts: ProposalProduct[]) => {
    dispatch(menuConstructorActions.setSelectedProducts(selectedProducts));
  };

  const handleAddToAllOptions = () => {
    dispatch(menuConstructorActions.addAllProductsToMenu());
  };

  const handleUpdateProductConfig = (productId: string, data: Partial<ProductConfig>) => {
    dispatch(menuConstructorActions.updateProductConfiguration({ data, productId }));
  };

  const handleUpdateProductConfigInMenu = (productId: string, data: Partial<ProductConfig>, menuName: string) => {
    dispatch(menuConstructorActions.updateProductConfigurationInMenu({ data, menuName, productId }));
  };

  const handleOpenProductsDialog = () => {
    handleProposalProductsDialogOpen({
      products: allProducts,
      onConfirm: handleProductsDialogConfirm,
      selectedProposalsProductsIds: selectedProducts?.map(p => p.proposalProductId),
    });
  };

  const handleUnselectProduct = (productId: string) => {
    dispatch(menuConstructorActions.unselectProduct({ productId }));
  };

  return (
    <CardContainer
      title="Products"
      isLoading={isLoading}
      actionBtnProps={{ isHidden: !hasWriteAccess }}
      actionBtn={
        <Button
          startIcon={<Add />}
          loadingPosition="start"
          loading={isProductsLoading}
          onClick={handleOpenProductsDialog}
        >
          {!!selectedProducts.length ? 'Add more' : 'Add product'}
        </Button>
      }
    >
      <Box className={classes.container}>
        <SelectedProductsList products={selectedProducts} onRemove={handleUnselectProduct} />

        {!selectedMenuOption &&
          selectedProducts.map(product => (
            <ProposalProductsCard
              type="simple"
              product={product}
              products={allProducts}
              key={product.proposalProductId}
              mode={ProposalProductCardModes.Constructor}
              prisingConfigByVendor={prisingConfigByVendor}
              factsToCheck={selectedProductsFactsForCheck?.[getProductFactHashMapKey(product)]}
              productConfiguration={proposalProductConfigurations[product.proposalProductId] ?? {}}
              updateProductConfiguration={updateValues => {
                handleUpdateProductConfig(product.proposalProductId, updateValues);
              }}
            />
          ))}

        {!selectedMenuOption && !!selectedProducts.length && (
          <Tooltip title="Adds all products & their current configuration to every available menu option in the template">
            <Button variant="outlined" onClick={handleAddToAllOptions} disabled={isMinMaxRetailCostInvalid}>
              Apply Product(s) to All Options
            </Button>
          </Tooltip>
        )}

        {(selectedMenuOption?.products || []).map((product: ProposalProduct) => {
          const fullProduct = allProducts.find(p => p.proposalProductId === product.proposalProductId) ?? product;
          return (
            <ProposalProductsCard
              type="forEdit"
              product={fullProduct}
              products={allProducts}
              mode={ProposalProductCardModes.Constructor}
              key={`forEdit-${product.proposalProductId}`}
              prisingConfigByVendor={prisingConfigByVendor}
              factsToCheck={selectedProductsFactsForCheck[getProductFactHashMapKey(product)]}
              updateProductConfiguration={updateValues => {
                handleUpdateProductConfigInMenu(product.proposalProductId, updateValues, selectedMenuOption.name);
              }}
              productConfiguration={
                proposalProductConfigurationsInMenu?.[selectedMenuOption.name]?.[product.proposalProductId] ?? {}
              }
            />
          );
        })}
      </Box>
    </CardContainer>
  );
};

const useStyles = makeStyles({
  container: {
    gap: '0.5rem',
    display: 'flex',
    maxHeight: '800px',
    overflowY: 'auto',
    flexDirection: 'column',
  },
});
